import { gql } from '@apollo/client';

// --- Fragments ---

export const SCHEDULE_LIST_FIELDS = gql`
  fragment ScheduleListFields on Schedule {
    _id
    name
    teamId
    path
    tags
    defaultAssetId
    createdAt
    lastUpdatedDate
  }
`;

export const SCHEDULE_DETAIL_FIELDS = gql`
  fragment ScheduleDetailFields on ScheduleDetailResponse {
    _id
    name
    teamId
    path
    tags
    defaultAssetId
    createdAt
    lastUpdatedDate
    accountId
    isHide
    type
    defaultAssetGlobal
    createdBy
    lastUpdatedBy
    parentGroupInfo {
      _id
      name
      path
      teamId
      isFolderSecurityEdit
      isFolderSecurityView
      securities {
        userId
        role
      }
      securityTeams
      securitiesDetail {
        name
        userId
      }
      securityTeamsDetail {
        name
        _id
      }
    }
  }
`;

export const SCHEDULE_ITEM_FIELDS = gql`
  fragment ScheduleItemFields on ScheduleItems {
    _id
    scheduleId
    parentScheduleId
    name
    type
    assetId
    playlistId
    single
    isHide
    range {
      startDate
      endDate
    }
    color
    eventTextColor
    scale
    stretchAsset
    documentDuration
    assetAppType
    createdAt
    lastUpdated
    repeatObject {
      text
      id
      type
      repeat
      rrule
    }
    feature {
      power
      powerOff
      volume
      brightness
      isMute
      hdmiLock
      comCommands
      isEnableBrightness
      isEnableVolume
      isEnableMute
      isEnablePowerOff
      isEnableComCommands
    }
  }
`;

// --- Queries ---

// NOTE: The SCHEDULES_QUERY intentionally inlines field selections instead of
// using the ...ScheduleListFields fragment. The backend's schedule resolver uses
// extractFields(info.fieldNodes) to build MongoDB projections, but it doesn't
// resolve GraphQL fragment spreads — it reads AST selection names directly.
// Using a named fragment here would cause the backend to project only
// {ScheduleListFields: 1} instead of the actual fields, returning empty results.
export const SCHEDULES_QUERY = gql`
  query Schedules(
    $first: Int
    $after: String
    $teamId: String
    $query: QueryScheduleInput
    $sort: ScheduleSort
  ) {
    schedules(first: $first, after: $after, teamId: $teamId, query: $query, sort: $sort) {
      page {
        edges {
          cursor
          node {
            _id
            name
            teamId
            path
            tags
            defaultAssetId
            createdAt
            lastUpdatedDate
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      pageData {
        total
      }
      extensions {
        groups {
          _id
          name
          type
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          securities {
            userId
            role
          }
          securityTeams
          sharedLink
        }
      }
    }
  }
`;

// Batch fetch schedules by IDs for realtime subscription cache sync.
// Returns Schedule[] (same __typename as list) so Apollo auto-merges into list cache.
export const SCHEDULES_BY_IDS = gql`
  query SchedulesByIds($ids: [String!]!, $teamId: String) {
    schedulesByIds(ids: $ids, teamId: $teamId) {
      _id
      name
      teamId
      path
      tags
      defaultAssetId
      createdAt
      lastUpdatedDate
    }
  }
`;

export const SCHEDULE_DETAIL_QUERY = gql`
  query GetScheduleDetail($_id: String!, $teamId: String!) {
    getScheduleDetail(_id: $_id, teamId: $teamId) {
      ...ScheduleDetailFields
    }
  }
  ${SCHEDULE_DETAIL_FIELDS}
`;

// NOTE: SCHEDULE_ITEMS_QUERY intentionally inlines field selections instead of
// using the ...ScheduleItemFields fragment. The backend's scheduleItems resolver
// uses extractFields(info.fieldNodes) which doesn't resolve GraphQL fragments.
// Same issue as SCHEDULES_QUERY above.
export const SCHEDULE_ITEMS_QUERY = gql`
  query ScheduleItems($query: QueryScheduleItemsInput!, $teamId: String, $first: Int) {
    scheduleItems(query: $query, teamId: $teamId, first: $first) {
      page {
        edges {
          cursor
          node {
            _id
            scheduleId
            parentScheduleId
            name
            type
            assetId
            playlistId
            single
            isHide
            range {
              startDate
              endDate
            }
            color
            eventTextColor
            scale
            stretchAsset
            documentDuration
            assetAppType
            createdAt
            lastUpdated
            repeatObject {
              text
              id
              type
              repeat
              rrule
            }
            feature {
              power
              volume
              brightness
              isMute
              hdmiLock
              comCommands
              isEnableBrightness
              isEnableVolume
              isEnableMute
              isEnablePowerOff
              isEnableComCommands
              powerOff
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const SCHEDULE_DEFAULT_QUERY = gql`
  query ScheduleDefault($teamId: String!, $scheduleId: String) {
    scheduleDefault(teamId: $teamId, scheduleId: $scheduleId) {
      type
      id
      name
    }
  }
`;

export const SCHEDULE_REPEAT_CHOICES_QUERY = gql`
  query ScheduleRepeatObjectChoices($startDate: DateTime!) {
    scheduleRepeatObjectChoices(startDate: $startDate) {
      text
      id
      repeat
      rrule
    }
  }
`;

// Separate query for schedule folder groups -- fetches folders at a specific path
// for folder tree navigation (e.g., MoveToFolderDialog). Uses the same `groups`
// resolver as DEVICE_GROUPS_QUERY / ASSET_GROUPS_QUERY, filtered by type SCHEDULE.
export const SCHEDULE_GROUPS_QUERY = gql`
  query ScheduleGroups(
    $query: QueryGroupInput
    $first: Int
    $after: String
    $sort: GroupSort
    $teamId: String
  ) {
    groups(query: $query, first: $first, after: $after, sort: $sort, teamId: $teamId) {
      page {
        edges {
          cursor
          node {
            _id
            name
            type
            path
            teamId
            createdAt
            lastUpdatedDate
            isFolderSecurityEdit
            isFolderSecurityView
            securities {
              userId
              role
            }
            securityTeams
            securitiesDetail {
              name
              userId
            }
            securityTeamsDetail {
              name
              _id
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      pageData {
        total
      }
    }
  }
`;

// --- Mutations ---

export const SAVE_SCHEDULE = gql`
  mutation SaveSchedule($teamId: String!, $payload: ScheduleInput!) {
    saveSchedule(teamId: $teamId, payload: $payload) {
      ...ScheduleDetailFields
    }
  }
  ${SCHEDULE_DETAIL_FIELDS}
`;

export const CLONE_SCHEDULE = gql`
  mutation CloneSchedule($teamId: String!, $_id: String!) {
    cloneSchedule(teamId: $teamId, _id: $_id) {
      ...ScheduleDetailFields
    }
  }
  ${SCHEDULE_DETAIL_FIELDS}
`;

export const ADD_SCHEDULE_ITEM = gql`
  mutation AddScheduleItem($teamId: String!, $payload: AddScheduleItemInput!, $force: Boolean) {
    addScheduleItem(teamId: $teamId, payload: $payload, force: $force) {
      ...ScheduleItemFields
    }
  }
  ${SCHEDULE_ITEM_FIELDS}
`;

export const UPDATE_SCHEDULE_ITEM = gql`
  mutation UpdateScheduleItem(
    $teamId: String!
    $payload: UpdateScheduleItemInput!
    $force: Boolean
    $scope: APPLY_SCHEDULE_ITEM_SCOPES!
  ) {
    updateScheduleItem(teamId: $teamId, payload: $payload, force: $force, scope: $scope) {
      ...ScheduleItemFields
    }
  }
  ${SCHEDULE_ITEM_FIELDS}
`;

export const REMOVE_SCHEDULE_ITEM = gql`
  mutation RemoveScheduleItem(
    $teamId: String!
    $payload: RemoveScheduleItemInput!
    $scope: APPLY_SCHEDULE_ITEM_SCOPES!
  ) {
    removeScheduleItem(teamId: $teamId, payload: $payload, scope: $scope)
  }
`;

// Custom schedule item mutations for operational schedules.
// Uses AddCustomScheduleItemInput (has name + feature, no assetId/playlistId).
// Must inline fields — backend extractFields doesn't resolve fragments.
export const ADD_CUSTOM_SCHEDULE_ITEM = gql`
  mutation AddCustomScheduleItem(
    $teamId: String!
    $payload: AddCustomScheduleItemInput!
    $force: Boolean
  ) {
    addCustomScheduleItem(teamId: $teamId, payload: $payload, force: $force) {
      _id
      scheduleId
      name
      type
      single
      isHide
      range {
        startDate
        endDate
      }
      color
      eventTextColor
      createdAt
      lastUpdated
      repeatObject {
        text
        id
        type
        repeat
        rrule
      }
      feature {
        power
        volume
        brightness
        isMute
        hdmiLock
        comCommands
        isEnableBrightness
        isEnableVolume
        isEnableMute
        isEnablePowerOff
        isEnableComCommands
        powerOff
      }
    }
  }
`;

export const UPDATE_CUSTOM_SCHEDULE_ITEM = gql`
  mutation UpdateCustomScheduleItem(
    $teamId: String!
    $payload: UpdateCustomScheduleItemInput!
    $scope: APPLY_SCHEDULE_ITEM_SCOPES!
    $force: Boolean
  ) {
    updateCustomScheduleItem(teamId: $teamId, payload: $payload, scope: $scope, force: $force) {
      _id
      scheduleId
      name
      type
      single
      isHide
      range {
        startDate
        endDate
      }
      color
      eventTextColor
      createdAt
      lastUpdated
      repeatObject {
        text
        id
        type
        repeat
        rrule
      }
      feature {
        power
        volume
        brightness
        isMute
        hdmiLock
        comCommands
        isEnableBrightness
        isEnableVolume
        isEnableMute
        isEnablePowerOff
        isEnableComCommands
        powerOff
      }
    }
  }
`;

// Lightweight Atlas Search query for schedule picker combobox.
// Returns flat array (not Relay-paginated). Only fields needed for display.
export const SEARCH_SCHEDULES_PICKER = gql`
  query SearchSchedulesPicker($teamId: String, $input: SearchInput!) {
    searchSchedules(teamId: $teamId, input: $input) {
      _id
      name
      path
      teamId
    }
  }
`;

export const UPDATE_SCHEDULE_DEFAULT = gql`
  mutation UpdateScheduleDefault($teamId: String!, $payload: ScheduleDefaultInput!) {
    updateScheduleDefault(teamId: $teamId, payload: $payload)
  }
`;
